import { useState } from 'react';
import { tokens } from '../lib/api';

type Cakisma = {
  kod: string;
  satirlar: { satir: number; ad: string; ebat: string; yuzey: string; stok: number | null }[];
};
type Onizleme = { onizleme: true; okunan: number; aktarilabilir: number; yeniUrun?: number; guncellenecek?: number; stokDegisecek?: number; bicim?: string; cakisan: Cakisma[]; uyari: string[] };
type Sonuc = { ok: true; okunan: number; yeni: number; guncel: number; sayim: number; cakisan: Cakisma[]; uyari: string[] };

/**
 * Excel'den içe aktarım — mockup'taki "⇪ Excel'den içe aktar" düğmesi.
 *
 * Önce ÖNİZLEME zorunlu: çakışan kod ve tutarsızlıklar görülmeden yazma
 * yapılmaz. Çakışan kodlar aktarılmaz (ART kodu tekil olmak zorunda), aynen
 * listelenir ki Mehmetcan ayırsın.
 */
export default function ExcelAktar({ kapat, bitti }: { kapat: () => void; bitti: () => void }) {
  const [dosya, setDosya] = useState<File | null>(null);
  const [onizleme, setOnizleme] = useState<Onizleme | null>(null);
  const [sonuc, setSonuc] = useState<Sonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  async function gonder(uygula: boolean) {
    if (!dosya) return;
    setHata(null); setBekliyor(true);
    try {
      const fd = new FormData();
      fd.append('dosya', dosya);
      const res = await fetch(`/api/urunler/ice-aktar${uygula ? '' : '?onizleme=1'}`, {
        method: 'POST',
        headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {},
        body: fd,
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error ?? 'Aktarım başarısız');
      if (uygula) setSonuc(j as Sonuc);
      else setOnizleme(j as Onizleme);
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Aktarım başarısız');
    } finally {
      setBekliyor(false);
    }
  }

  const cakisan = sonuc?.cakisan ?? onizleme?.cakisan ?? [];
  const uyari = sonuc?.uyari ?? onizleme?.uyari ?? [];

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside
        className="ccd-drawer" onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label="Excel'den içe aktar"
      >
        <div className="ccd-stack">
          <div className="ccd-row-between">
            <div>
              <div className="ccd-eyebrow">Stok</div>
              <h2 className="ccd-heading">Excel'den içe aktar</h2>
            </div>
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
          </div>

          {!sonuc && (
            <div className="ccd-card ccd-stack">
              <div className="ccd-field">
                <label className="ccd-field-label" htmlFor="dsy">Stok dosyası (.xlsx)</label>
                <input
                  id="dsy" className="ccd-input" type="file" accept=".xlsx,.xlsm,.xls"
                  onChange={(e) => { setDosya(e.target.files?.[0] ?? null); setOnizleme(null); }}
                />
                <div className="ccd-field-hint">
                  Beklenen sütunlar: kod · ebat · ad · yüzey · stok · durum · liste fiyatı · bayi fiyatı.
                  Seri başlıkları (kodsuz satırlar) otomatik tanınır.
                </div>
              </div>
              <div className="ccd-row">
                <button className="ccd-btn ccd-btn-sm" disabled={!dosya || bekliyor}
                        onClick={() => gonder(false)}>
                  {bekliyor ? 'Okunuyor…' : 'Önizle'}
                </button>
                <button
                  className="ccd-btn ccd-btn-primary ccd-btn-sm"
                  disabled={!onizleme || bekliyor}
                  onClick={() => gonder(true)}
                  title={onizleme ? '' : 'Önce önizleme yapın'}
                >
                  Aktar
                </button>
              </div>
            </div>
          )}

          {hata && (
            <div className="ccd-banner ccd-banner-danger">
              <span className="ccd-banner-icon" aria-hidden="true">!</span>
              <span>{hata}</span>
            </div>
          )}

          {onizleme && !sonuc && (
            <div className="ccd-banner">
              <span className="ccd-banner-icon" aria-hidden="true">👁</span>
              <div>
                <div style={{ fontWeight: 600 }}>
                  {onizleme.okunan} satır okundu · {onizleme.aktarilabilir} tanesi aktarılabilir
                  {onizleme.yeniUrun != null && ` · ${onizleme.yeniUrun} yeni ürün · ${onizleme.guncellenecek} güncelleme · ${onizleme.stokDegisecek} üründe stok değişecek`}
                  {onizleme.bicim === 'panel' && ' · panelden indirilen dosya'}
                </div>
                <div className="ccd-meta">Henüz hiçbir şey yazılmadı. Aşağıyı görüp "Aktar"a basın.</div>
              </div>
            </div>
          )}

          {sonuc && (
            <div className="ccd-banner">
              <span className="ccd-banner-icon" aria-hidden="true">✓</span>
              <div>
                <div style={{ fontWeight: 600 }}>
                  {sonuc.yeni} yeni ürün · {sonuc.guncel} güncelleme · {sonuc.sayim} stok düzeltmesi
                </div>
                <div className="ccd-meta">
                  Stok farkları sayım hareketi olarak yazıldı, geçmiş silinmedi.
                </div>
              </div>
            </div>
          )}

          {cakisan.length > 0 && (
            <div className="ccd-card ccd-stack">
              <div className="ccd-fieldset-title">
                Çakışan kod ({cakisan.length}) — aktarılmadı
              </div>
              <p className="ccd-meta" style={{ margin: 0 }}>
                Aynı ART kodu birden fazla ürüne verilmiş. Kod tekil olmak zorunda;
                birini ezmek stoğu yanlış gösterirdi. İkinci ürüne yeni bir kod verip
                dosyayı tekrar yükleyin.
              </p>
              {cakisan.map((c) => (
                <div key={c.kod}>
                  <div className="ccd-mono" style={{ fontWeight: 600 }}>{c.kod}</div>
                  {c.satirlar.map((r) => (
                    <div key={r.satir} className="ccd-meta">
                      satır {r.satir} · {r.ad} · {r.ebat} · {r.yuzey} · stok {r.stok}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {uyari.length > 0 && (
            <div className="ccd-card">
              <div className="ccd-fieldset-title">Tutarsızlık ({uyari.length}) — aktarıldı, bakılmalı</div>
              <ul className="ccd-stack-sm" style={{ margin: 0, paddingLeft: '1.1em' }}>
                {uyari.map((u) => (
                  <li key={u} className="ccd-meta">{u}</li>
                ))}
              </ul>
            </div>
          )}

          {sonuc && (
            <button className="ccd-btn ccd-btn-primary" onClick={bitti}>Listeye dön</button>
          )}
        </div>
      </aside>
    </div>
  );
}
